import 'assets/css/modal.css';
import 'styles/custom.css';
import * as React from 'react';
import { getRegionNames, getDuplicateSummary, getDuplicateAnalysis, markDuplicatesComplete } from '../components/Requests/Requests';
import {
    StandardScreen,
    StandardSearchBox,
    StandardDropdown,
    StandardButton,
    StatusMessage,
    StandardTextField,
    StandardTable,
    StandardTableHeader,
    StandardTableBody,
    StandardTableRow,
    StandardTableCell,
} from '../components/Layout';
import { PageLayout } from '../components/Layout/BaseComponents.jsx';
import Theme from '../styles/theme.js';
import { Dialog, DialogType, DialogFooter } from 'office-ui-fabric-react/lib/Dialog';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';

const PAGE_SIZE = 25;

function fmtGap(ms) {
    if (ms == null) return '—';
    if (ms < 1000) return ms + 'ms';
    if (ms < 60000) return (ms / 1000).toFixed(1) + 's';
    if (ms < 3600000) return Math.round(ms / 60000) + 'm';
    if (ms < 86400000) return (ms / 3600000).toFixed(1) + 'h';
    return Math.round(ms / 86400000) + 'd';
}

function dotColor(ms) {
    if (ms == null) return Theme.colors.border;
    if (ms < 10000) return Theme.colors.error;
    if (ms < 3600000) return Theme.colors.warning;
    return Theme.colors.success;
}

function badgeStyle(count) {
    if (count > 500) return { background: '#fde8e8', color: Theme.colors.error };
    if (count > 20)  return { background: '#fff3e0', color: Theme.colors.warning };
    return { background: '#e8f5e9', color: Theme.colors.success };
}

function parsePerDay(perDayCounts) {
    if (!perDayCounts) return [];
    return perDayCounts.split(',').map(part => {
        const [day, count] = part.split(':');
        return { day: day.trim(), count: parseInt(count, 10) };
    });
}

function parseSampleTimestamps(raw) {
    if (!raw) return [];
    return raw.split(',').map(s => s.trim()).filter(Boolean);
}

function computeGapsFromSamples(timestamps) {
    return timestamps.map((ts, i) => {
        if (i === 0) return null;
        const diff = new Date(ts) - new Date(timestamps[i - 1]);
        return diff;
    });
}

const styles = {
    analysisTable: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: Theme.typography.fontSize.small,
        tableLayout: 'fixed',
    },
    th: {
        background: Theme.colors.background,
        padding: '8px 12px',
        textAlign: 'left',
        fontWeight: Theme.typography.fontWeight.medium,
        borderBottom: `1px solid ${Theme.colors.border}`,
        fontSize: Theme.typography.fontSize.small,
        color: '#555',
    },
    td: {
        padding: '10px 12px',
        borderBottom: `1px solid ${Theme.colors.border}`,
        verticalAlign: 'middle',
    },
    expandTd: {
        padding: 0,
        borderBottom: `1px solid ${Theme.colors.border}`,
    },
    expandInner: {
        background: Theme.colors.background,
        padding: '12px 16px',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
    },
    expandSection: {
        fontSize: Theme.typography.fontSize.small,
    },
    expandH: {
        fontSize: Theme.typography.fontSize.small,
        fontWeight: Theme.typography.fontWeight.medium,
        color: '#555',
        marginBottom: '8px',
    },
    aggCards: {
        display: 'flex',
        gap: '8px',
        marginBottom: '10px',
    },
    aggCard: {
        flex: 1,
        background: Theme.colors.cardBackground,
        border: `1px solid ${Theme.colors.border}`,
        borderRadius: '4px',
        padding: '6px 10px',
    },
    aggLabel: {
        fontSize: '11px',
        color: '#888',
        marginBottom: '2px',
    },
    aggVal: {
        fontSize: Theme.typography.fontSize.normal,
        fontWeight: Theme.typography.fontWeight.medium,
        fontFamily: 'monospace',
    },
    tsList: {
        fontSize: '11px',
        fontFamily: 'monospace',
        color: '#555',
        lineHeight: 1.9,
    },
    dayList: {
        fontSize: '11px',
        fontFamily: 'monospace',
        color: '#555',
        lineHeight: 1.9,
    },
    pagination: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 12px',
        borderTop: `1px solid ${Theme.colors.border}`,
        fontSize: Theme.typography.fontSize.small,
        color: '#555',
    },
    pageBtn: {
        padding: '3px 10px',
        border: `1px solid ${Theme.colors.border}`,
        borderRadius: '4px',
        background: 'transparent',
        fontSize: '12px',
        cursor: 'pointer',
        marginLeft: '4px',
    },
    pageBtnActive: {
        background: '#e3f2fd',
        color: Theme.colors.primary,
        borderColor: Theme.colors.primary,
    },
    statCard: {
        display: 'inline-block',
        background: Theme.colors.background,
        border: `1px solid ${Theme.colors.border}`,
        borderRadius: '4px',
        padding: '10px 16px',
        marginRight: '12px',
        marginBottom: '16px',
    },
    statLabel: {
        fontSize: '11px',
        color: '#888',
        marginBottom: '4px',
    },
    statVal: {
        fontSize: Theme.typography.fontSize.large,
        fontWeight: Theme.typography.fontWeight.medium,
    },
};

function DayBars({ perDayCounts }) {
    const days = parsePerDay(perDayCounts);
    if (days.length === 0) return <span style={{ color: '#aaa' }}>—</span>;
    const max = Math.max(...days.map(d => d.count));
    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '22px', overflow: 'hidden', maxWidth: '100%' }}>
                {days.map((d, i) => {
                    const h = Math.max(3, Math.round((d.count / max) * 22));
                    return (
                        <div
                            key={i}
                            title={`${d.day}: ${d.count}`}
                            style={{
                                width: '10px',
                                height: `${h}px`,
                                background: Theme.colors.primary,
                                borderRadius: '2px 2px 0 0',
                                flexShrink: 0,
                            }}
                        />
                    );
                })}
            </div>
            <div style={{ fontSize: '11px', color: '#aaa', marginTop: '3px' }}>
                {days.length} day{days.length !== 1 ? 's' : ''}
            </div>
        </div>
    );
}

function GapStats({ minGapMs, avgGapMs, maxGapMs }) {
    const rows = [
        { label: 'min', val: minGapMs },
        { label: 'avg', val: avgGapMs },
        { label: 'max', val: maxGapMs },
    ];
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            {rows.map(({ label, val }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{
                        width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0,
                        background: dotColor(val),
                    }} />
                    <span style={{ fontSize: '11px', color: '#aaa', width: '26px' }}>{label}</span>
                    <span style={{ fontSize: '12px', fontFamily: 'monospace' }}>{fmtGap(val)}</span>
                </div>
            ))}
        </div>
    );
}

function ExpandedRow({ item }) {
    const timestamps = parseSampleTimestamps(item.sampleTimestamps);
    const gaps = computeGapsFromSamples(timestamps);
    const days = parsePerDay(item.perDayCounts);
    const sampleMore = item.requestCount - timestamps.length;

    return (
        <div style={styles.expandInner}>
            <div style={styles.expandSection}>
                <div style={styles.expandH}>Gap summary</div>
                <div style={styles.aggCards}>
                    {[
                        { label: 'Min', val: item.minGapMs },
                        { label: 'Avg', val: item.avgGapMs },
                        { label: 'Max', val: item.maxGapMs },
                    ].map(({ label, val }) => (
                        <div key={label} style={styles.aggCard}>
                            <div style={styles.aggLabel}>{label}</div>
                            <div style={{ ...styles.aggVal, color: dotColor(val) }}>{fmtGap(val)}</div>
                        </div>
                    ))}
                </div>
                <div style={styles.expandH}>First {timestamps.length} timestamps</div>
                <div style={styles.tsList}>
                    {timestamps.map((ts, i) => (
                        <div key={i}>
                            {ts}
                            {gaps[i] != null && (
                                <span style={{ color: dotColor(gaps[i]), marginLeft: '8px' }}>
                                    → {fmtGap(gaps[i])}
                                </span>
                            )}
                        </div>
                    ))}
                    {sampleMore > 0 && (
                        <div style={{ color: '#aaa', fontStyle: 'italic', marginTop: '4px' }}>
                            {sampleMore.toLocaleString()} more not shown
                        </div>
                    )}
                </div>
            </div>
            <div style={styles.expandSection}>
                <div style={styles.expandH}>Daily breakdown</div>
                <div style={styles.dayList}>
                    {days.map((d, i) => (
                        <div key={i}>{d.day}: {d.count.toLocaleString()}</div>
                    ))}
                </div>
                <div style={{ ...styles.expandH, marginTop: '12px' }}>Item details</div>
                <div style={{ fontSize: '11px', color: '#888' }}>
                    <div>Type: <span style={{ fontFamily: 'monospace', color: '#555' }}>{item.itemType}</span></div>
                    <div style={{ marginTop: '4px' }}>Code: <span style={{ fontFamily: 'monospace', color: '#555' }}>{item.itemCode}</span></div>
                </div>
            </div>
        </div>
    );
}

class DuplicateRequests extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            loadingClear: false,
            loadingAnalysis: false,
            error: '',
            clearError: '',
            analysisError: '',
            message: '',
            clearMessage: '',
            regionNames: [],
            regionNameChosen: '',
            clearSpHostUrl: '',
            reason: '',
            hideClearDialog: true,
            summary: [],
            analysisHost: null,
            analysisItems: [],
            analysisTotalItems: 0,
            analysisTotalDuplicates: 0,
            analysisPage: 1,
            expandedRows: new Set(),
        };
    }

    componentDidMount() {
        this.getRegionNames();
    }

    async getRegionNames() {
        this.setState({ loading: true });
        try {
            const response = await getRegionNames(this.props.accessToken);
            if (response.status === 200) {
                const data = await response.json();
                this.setState({ loading: false, regionNames: data.map(item => ({ key: item, text: item })) });
            } else {
                this.setState({ loading: false, error: 'Failed to load environments.' });
            }
        } catch {
            this.setState({ loading: false, error: 'Failed to load environments.' });
        }
    }

    onSelectRegion = (event, item) => {
        this.setState({
            regionNameChosen: item.key,
            error: '', clearError: '', message: '', clearMessage: '',
            summary: [], analysisHost: null, analysisItems: [],
        });
    };

    async getSummary() {
        const { regionNameChosen } = this.state;
        this.setState({ loading: true, error: '', message: '', summary: [], analysisHost: null, analysisItems: [] });
        try {
            const response = await getDuplicateSummary(this.props.accessToken, { regionName: regionNameChosen });
            if (response.status === 200) {
                const data = await response.json();
                this.setState({ loading: false, summary: data, message: data.length === 0 ? 'No duplicates found.' : '' });
            } else {
                const text = await response.text();
                this.setState({ loading: false, error: text || 'Failed to retrieve summary.' });
            }
        } catch {
            this.setState({ loading: false, error: 'Failed to retrieve summary.' });
        }
    }

    async analyzeHost(spHostUrl, page = 1) {
        const { regionNameChosen } = this.state;
        this.setState({ loadingAnalysis: true, analysisError: '', analysisHost: spHostUrl, analysisPage: page, expandedRows: new Set() });
        try {
            const response = await getDuplicateAnalysis(this.props.accessToken, {
                regionName: regionNameChosen,
                spHostUrl,
                page,
                pageSize: PAGE_SIZE,
            });
            if (response.status === 200) {
                const data = await response.json();
                const totalItems = data.length > 0 ? data[0].totalItems : 0;
                const totalDuplicates = data.length > 0 ? data[0].totalDuplicateRequests : 0;
                this.setState({ loadingAnalysis: false, analysisItems: data, analysisTotalItems: totalItems, analysisTotalDuplicates: totalDuplicates });
            } else {
                const text = await response.text();
                this.setState({ loadingAnalysis: false, analysisError: text || 'Failed to load analysis.' });
            }
        } catch {
            this.setState({ loadingAnalysis: false, analysisError: 'Failed to load analysis.' });
        }
    }

    toggleRow(index) {
        this.setState(prev => {
            const next = new Set(prev.expandedRows);
            next.has(index) ? next.delete(index) : next.add(index);
            return { expandedRows: next };
        });
    }

    async clearDuplicates() {
        const { regionNameChosen, clearSpHostUrl, reason } = this.state;
        this.setState({ loadingClear: true, clearError: '', clearMessage: '' });
        try {
            const response = await markDuplicatesComplete(this.props.accessToken, {
                regionName: regionNameChosen,
                spHostUrl: clearSpHostUrl || null,
                reason,
            });
            if (response.status === 200) {
                const data = await response.json();
                this.setState({ loadingClear: false, clearMessage: data.message, summary: [], analysisHost: null, analysisItems: [] });
            } else if (response.status === 401) {
                this.setState({ loadingClear: false, clearError: 'You are not permitted to use this feature. You must request admin permissions.' });
            } else {
                const text = await response.text();
                this.setState({ loadingClear: false, clearError: text || 'Something went wrong.' });
            }
        } catch {
            this.setState({ loadingClear: false, clearError: 'Something went wrong.' });
        }
    }

    renderAnalysisTable() {
        const {
            loadingAnalysis, analysisError, analysisItems,
            analysisTotalItems, analysisTotalDuplicates,
            analysisPage, analysisHost, expandedRows,
        } = this.state;

        const totalPages = Math.ceil(analysisTotalItems / PAGE_SIZE);
        const offsetStart = (analysisPage - 1) * PAGE_SIZE + 1;
        const offsetEnd = Math.min(analysisPage * PAGE_SIZE, analysisTotalItems);

        return (
            <StandardScreen
                title={`Analysis — ${analysisHost}`}
                description="Per-item breakdown of duplicate requests. Expand a row for full detail."
                loading={loadingAnalysis}
            >
                {analysisError && <StatusMessage type="error">{analysisError}</StatusMessage>}

                {!loadingAnalysis && analysisItems.length > 0 && (
                    <>
                        <div style={{ marginBottom: '16px' }}>
                            <div style={styles.statCard}>
                                <div style={styles.statLabel}>Items with duplicates</div>
                                <div style={styles.statVal}>{analysisTotalItems.toLocaleString()}</div>
                            </div>
                            <div style={styles.statCard}>
                                <div style={styles.statLabel}>Total duplicate requests</div>
                                <div style={styles.statVal}>{analysisTotalDuplicates.toLocaleString()}</div>
                            </div>
                        </div>

                        <div style={{ border: `1px solid ${Theme.colors.border}`, borderRadius: '4px', overflow: 'hidden' }}>
                            <table style={styles.analysisTable}>
                                <colgroup>
                                    <col style={{ width: '80px' }} />
                                    <col />
                                    <col style={{ width: '160px' }} />
                                    <col style={{ width: '160px' }} />
                                    <col style={{ width: '30px' }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Count</th>
                                        <th style={styles.th}>Item</th>
                                        <th style={styles.th}>Daily pattern</th>
                                        <th style={styles.th}>Gap (min / avg / max)</th>
                                        <th style={styles.th} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {analysisItems.map((item, i) => {
                                        const expanded = expandedRows.has(i);
                                        return (
                                            <React.Fragment key={i}>
                                                <tr
                                                    onClick={() => this.toggleRow(i)}
                                                    style={{ cursor: 'pointer', background: expanded ? Theme.colors.background : Theme.colors.cardBackground }}
                                                >
                                                    <td style={styles.td}>
                                                        <span style={{
                                                            ...badgeStyle(item.requestCount),
                                                            display: 'inline-block',
                                                            padding: '2px 8px',
                                                            borderRadius: '4px',
                                                            fontSize: '12px',
                                                            fontWeight: Theme.typography.fontWeight.medium,
                                                        }}>
                                                            {item.requestCount.toLocaleString()}
                                                        </span>
                                                    </td>
                                                    <td style={styles.td}>
                                                        <div style={{ fontWeight: Theme.typography.fontWeight.medium, fontSize: Theme.typography.fontSize.small }}>
                                                            {item.itemType}
                                                        </div>
                                                        <div style={{ fontSize: '11px', color: '#aaa', fontFamily: 'monospace', marginTop: '2px' }}>
                                                            {item.itemCode}
                                                        </div>
                                                    </td>
                                                    <td style={{ ...styles.td, overflow: 'hidden' }}>
                                                        <DayBars perDayCounts={item.perDayCounts} />
                                                    </td>
                                                    <td style={styles.td}>
                                                        <GapStats minGapMs={item.minGapMs} avgGapMs={item.avgGapMs} maxGapMs={item.maxGapMs} />
                                                    </td>
                                                    <td style={{ ...styles.td, textAlign: 'center', color: '#aaa', fontSize: '16px' }}>
                                                        {expanded ? '▾' : '›'}
                                                    </td>
                                                </tr>
                                                {expanded && (
                                                    <tr>
                                                        <td colSpan={5} style={styles.expandTd}>
                                                            <ExpandedRow item={item} />
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>

                            <div style={styles.pagination}>
                                <span>Showing {offsetStart}–{offsetEnd} of {analysisTotalItems.toLocaleString()} items</span>
                                <div>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                                        <button
                                            key={p}
                                            onClick={() => this.analyzeHost(analysisHost, p)}
                                            style={{ ...styles.pageBtn, ...(p === analysisPage ? styles.pageBtnActive : {}) }}
                                        >
                                            {p}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {!loadingAnalysis && !analysisError && analysisItems.length === 0 && (
                    <StatusMessage type="success">No duplicate items found for this host.</StatusMessage>
                )}
            </StandardScreen>
        );
    }

    render() {
        const {
            loading, loadingClear, error, clearError, message, clearMessage,
            regionNames, regionNameChosen, clearSpHostUrl, reason, hideClearDialog,
            summary, analysisHost,
        } = this.state;

        const summaryColumns = ['SPHostUrl', 'Duplicates', ''];

        return (
            <PageLayout>
                <StandardScreen
                    title="Duplicate Requests — Summary"
                    description="View duplicate provisioning requests (RequestType 28) grouped by SPHostUrl."
                    loading={loading}
                >
                    <StandardDropdown
                        label="Environment"
                        selectedKey={regionNameChosen}
                        onChange={this.onSelectRegion}
                        placeholder="Select Environment"
                        options={regionNames}
                        disabled={!regionNames || regionNames.length === 0}
                    />

                    <StandardButton
                        text="Get Summary"
                        onClick={() => this.getSummary()}
                        disabled={!regionNameChosen}
                        size="wide"
                    />

                    {error && <StatusMessage type="error">{error}</StatusMessage>}
                    {message && <StatusMessage type="success">{message}</StatusMessage>}

                    {summary.length > 0 && (
                        <div style={{ marginTop: Theme.spacing.md }}>
                            <StandardTable>
                                <StandardTableHeader columns={summaryColumns} />
                                <StandardTableBody>
                                    {summary.map((row, i) => (
                                        <StandardTableRow key={row.spHostUrl} index={i}>
                                            <StandardTableCell>{row.spHostUrl}</StandardTableCell>
                                            <StandardTableCell>{row.duplicates.toLocaleString()}</StandardTableCell>
                                            <StandardTableCell>
                                                <button
                                                    onClick={() => this.analyzeHost(row.spHostUrl, 1)}
                                                    style={{
                                                        padding: '3px 10px',
                                                        border: `1px solid ${Theme.colors.primary}`,
                                                        borderRadius: '4px',
                                                        background: analysisHost === row.spHostUrl ? Theme.colors.primary : 'transparent',
                                                        color: analysisHost === row.spHostUrl ? '#fff' : Theme.colors.primary,
                                                        fontSize: '12px',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    Analyse
                                                </button>
                                            </StandardTableCell>
                                        </StandardTableRow>
                                    ))}
                                </StandardTableBody>
                            </StandardTable>
                        </div>
                    )}
                </StandardScreen>

                {analysisHost && this.renderAnalysisTable()}

                <StandardScreen
                    title="Duplicate Requests — Clear"
                    description="Mark duplicate requests as complete. Optionally scope to a single SPHostUrl; leave blank to process all hosts. Admin only."
                    loading={loadingClear}
                    loadingLabel={
                        <span style={{ fontSize: Theme.typography.fontSize.large }}>
                            <strong>Clearing duplicates...</strong>
                            <br />
                            <span style={{ color: Theme.colors.text }}>This may take several minutes.</span>
                        </span>
                    }
                >
                    <StandardSearchBox
                        label="SPHostUrl (optional)"
                        placeholder="Leave blank to clear all hosts"
                        value={clearSpHostUrl}
                        onChange={(newValue) => this.setState({ clearSpHostUrl: newValue, clearMessage: '', clearError: '' })}
                    />

                    <StandardTextField
                        label="Reason"
                        placeholder="Provide a reason for making this change"
                        value={reason}
                        onChange={(event, newValue) => this.setState({ reason: newValue })}
                        disabled={!regionNameChosen}
                    />

                    <StandardButton
                        text="Clear Duplicates"
                        onClick={() => this.setState({ hideClearDialog: false })}
                        disabled={!regionNameChosen || !reason}
                        size="wide"
                    />

                    <Dialog
                        hidden={hideClearDialog}
                        onDismiss={() => this.setState({ hideClearDialog: true })}
                        dialogContentProps={{
                            type: DialogType.normal,
                            title: 'Clear duplicate requests?',
                            subText: clearSpHostUrl
                                ? `This will autocomplete all duplicate requests for ${clearSpHostUrl}. This cannot be undone.`
                                : 'This will autocomplete duplicate requests across all hosts. This cannot be undone.',
                        }}
                        modalProps={{ isBlocking: true }}
                    >
                        <DialogFooter>
                            <PrimaryButton
                                onClick={() => { this.setState({ hideClearDialog: true }); this.clearDuplicates(); }}
                                text="Clear Duplicates"
                            />
                            <DefaultButton
                                onClick={() => this.setState({ hideClearDialog: true })}
                                text="Cancel"
                            />
                        </DialogFooter>
                    </Dialog>

                    {clearError && <StatusMessage type="error">{clearError}</StatusMessage>}
                    {clearMessage && <StatusMessage type="success">{clearMessage}</StatusMessage>}
                </StandardScreen>
            </PageLayout>
        );
    }
}

export default DuplicateRequests;
